import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { defer, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { UserService } from '../services/user.service';
import * as UserActions from '../actions/user.actions';
import { User, UserActionSuccessTypeEnum } from '@sleipai/sleip-models';
import { Update } from '@ngrx/entity';

@Injectable()
export class UserEffects {
  private userService = inject(UserService);

  loadUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadUsers),
      switchMap(() => {
        return this.userService.getAllUsers().pipe(
          map((users: User[]) => {
            return UserActions.loadUsersSuccess({ users });
          })
        );
      })
    );
  });

  /**
   * TODO: update this function to check if the user exists in store before calling for the user.
   */
  loadUsersByIds$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadUsersByIds),
      mergeMap(({ uids }) => {
        return this.userService.getUsersByIds(uids).pipe(
          map((users: any[]) => {
            return UserActions.addUsers({ users });
          })
        );
      })
    );
  });

  loadSingleUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadSingleUser),
      mergeMap(({ uid }) => {
        const userStream = this.userService.getUser(uid);
        return userStream.pipe(
          map((user) => {
            return UserActions.addSingleUser({ user });
          })
        );
      })
    );
  });

  updateUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateUser),
      switchMap(({ user }) => {
        return this.userService.updateUser(user);
      }),
      map(() => {
        return UserActions.successUpdateUser();
      }),
      catchError((err) => of(UserActions.failedUpdateUser({ err })))
    );
  });

  createUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.createUser),
      switchMap(({ user }) =>
        this.userService.createUser(user).pipe(
          switchMap((response) => {
            return [
              UserActions.loadSingleUser({ uid: response.userId }),
              UserActions.createUserSuccess({
                userActionSuccessType: UserActionSuccessTypeEnum.USER_ADDED,
              }),
              UserActions.resetUserSavedSuccess(),
            ];
          }),
          catchError((error) => {
            return [
              UserActions.createUserFailure({ error }),
              UserActions.resetUserSavedSuccess(),
            ];
          })
        )
      )
    );
  });

  loadUsersBySearchTerm$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadUserBySearchTerm),
      mergeMap(({ searchTerm, userType }) => {
        return this.userService
          .searchUserTypeAndTerm(searchTerm, userType)
          .pipe(
            map((users: any[]) => {
              return UserActions.addUsers({ users });
            })
          );
      })
    );
  });

  loadUsersFromAlgoliaSidb$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadUsersFromAlgoliaSidb),
      mergeMap(({ queryConfig, updateTotalCount }) => {
        return this.userService.searchUsersInAlgoliaSidb(queryConfig).pipe(
          map((data) => {
            return UserActions.loadUsersFromAlgoliaSidbSuccess({
              data,
              updateTotalCount,
            });
          }),
          catchError((error) => {
            return [
              UserActions.loadUsersFromAlgoliaSidbFailure({
                error: error?.message || error,
              }),
            ];
          })
        );
      })
    );
  });

  loadUsersForInfiniteScrollList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadUsersForInfiniteScrollList),
      switchMap(({ reset = false, queryConfig }) => {
        return this.userService
          .fetchUsersForInfiniteScrollInDashBoard(reset, queryConfig)
          .pipe(
            map((result) => {
              return UserActions.loadUsersForInfiniteScrollListSuccess({
                users: result.users,
                hasMore: result.hasMore,
              });
            }),
            catchError((error) => {
              console.error(error);
              return of(
                UserActions.loadUsersForInfiniteScrollListFailure({
                  error:
                    error.message || 'An error occurred while loading users',
                })
              );
            })
          );
      })
    )
  );

  updateUserRemoteShareRightsSetting$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateUserRemoteShareRightsSetting),
      switchMap(({ userId, remoteShareRights }) =>
        defer(() =>
          this.userService.updateUserRemoteShareRightsSetting(
            userId,
            remoteShareRights
          )
        ).pipe(
          switchMap(() => {
            const updatePayload: Update<User> = {
              id: userId,
              changes: { shareRights: remoteShareRights },
            };
            return [
              UserActions.actionOnUserSuccess({
                update: updatePayload,
                userActionSuccessType:
                  UserActionSuccessTypeEnum.USER_REMOTE_SHARE_RIGHTS_SETTING_UPDATED,
              }),
              UserActions.actionOnUserSuccessReset(),
            ];
          }),
          catchError((error) =>
            of(UserActions.failedUpdateUser({ err: error }))
          )
        )
      )
    );
  });

  updateUserIsAdminSetting$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateUserIsAdminSetting),
      switchMap(({ userId, isAdmin }) =>
        defer(() =>
          this.userService.updateUserIsAdminSetting(userId, isAdmin)
        ).pipe(
          switchMap(() => {
            const updatePayload: Update<User> = {
              id: userId,
              changes: { admin: isAdmin },
            };
            return [
              UserActions.actionOnUserSuccess({
                update: updatePayload,
                userActionSuccessType:
                  UserActionSuccessTypeEnum.USER_IS_ADMIN_SETTING_UPDATED,
              }),
              UserActions.actionOnUserSuccessReset(),
            ];
          }),
          catchError((error) =>
            of(UserActions.failedUpdateUser({ err: error }))
          )
        )
      )
    );
  });

  updateUserBetaTesterSetting$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateUserBetaTesterSetting),
      switchMap(({ userId, betaTester }) =>
        defer(() =>
          this.userService.updateUserBetaTesterSetting(userId, betaTester)
        ).pipe(
          switchMap(() => {
            const updatePayload: Update<User> = {
              id: userId,
              changes: { betaTester: betaTester },
            };
            return [
              UserActions.actionOnUserSuccess({
                update: updatePayload,
                userActionSuccessType:
                  UserActionSuccessTypeEnum.USER_BETA_TESTER_SETTING_UPDATED,
              }),
              UserActions.actionOnUserSuccessReset(),
            ];
          }),
          catchError((error) =>
            of(UserActions.failedUpdateUser({ err: error }))
          )
        )
      )
    );
  });

  updateUserDesktopWebAppAccessSetting$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateUserDesktopWebAppAccessSetting),
      switchMap(({ userEmail, userId, desktopWebAppAccess }) =>
        this.userService.updateUserDesktopWebAppAccessSetting(userEmail).pipe(
          switchMap(() => {
            const updatePayload: Update<User> = {
              id: userId,
              changes: { webAppAccess: desktopWebAppAccess },
            };
            return [
              UserActions.actionOnUserSuccess({
                update: updatePayload,
                userActionSuccessType:
                  UserActionSuccessTypeEnum.USER_DESKTOP_WEBAPP_ACCESS_SETTING_UPDATED,
              }),
              UserActions.actionOnUserSuccessReset(),
            ];
          }),
          catchError((error) =>
            of(UserActions.failedUpdateUser({ err: error }))
          )
        )
      )
    );
  });

  updateUserGroupSharingSetting$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateUserGroupSharingSetting),
      switchMap(({ groupId, groupUser }) =>
        defer(() =>
          this.userService.updateUserGroupSharingSetting(groupId, groupUser)
        ).pipe(
          switchMap(() => {
            return [
              UserActions.actionOnUserSuccess({
                userActionSuccessType:
                  UserActionSuccessTypeEnum.USER_GROUP_SHARING_SETTING_UPDATED,
              }),
              UserActions.actionOnUserSuccessReset(),
            ];
          }),
          catchError((error) =>
            of(UserActions.failedUpdateUser({ err: error }))
          )
        )
      )
    );
  });

  updateUserName$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateUserName),
      switchMap(({ user, name }) => {
        const userId = user.id || (user.uid as string);

        return this.userService.updateUserName(userId, name).pipe(
          switchMap(() => {
            const updatePayload: Update<User> = {
              id: userId,
              changes: { ...user, name },
            };
            return [
              UserActions.actionOnUserSuccess({
                update: updatePayload,
                userActionSuccessType:
                  UserActionSuccessTypeEnum.USER_NAME_UPDATED,
              }),
              UserActions.actionOnUserSuccessReset(),
            ];
          }),
          catchError((error) => {
            console.error(error);
            return [
              UserActions.actionUserFailure({
                error: 'Something went wrong while updating the user name',
              }),
              UserActions.resetUserError(),
            ];
          })
        );
      })
    );
  });

  updateUserCountry$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateUserCountry),
      switchMap(({ user, country }) => {
        const userId = user.id || (user.uid as string);

        return this.userService.updateUserCountry(userId, country).pipe(
          switchMap(() => {
            const updatePayload: Update<User> = {
              id: userId,
              changes: { ...user, nationality: country },
            };
            return [
              UserActions.actionOnUserSuccess({
                update: updatePayload,
                userActionSuccessType:
                  UserActionSuccessTypeEnum.USER_COUNTRY_UPDATED,
              }),
              UserActions.actionOnUserSuccessReset(),
            ];
          }),
          catchError((error) => {
            console.error(error);
            return [
              UserActions.actionUserFailure({
                error: 'Something went wrong while updating the user country',
              }),
              UserActions.resetUserError(),
            ];
          })
        );
      })
    );
  });

  updateUserUserStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateUserUserStatus),
      switchMap(({ user, userStatus }) => {
        const userId = user.id || (user.uid as string);

        return this.userService.updateUserUserStatus(userId, userStatus).pipe(
          switchMap(() => {
            const updatePayload: Update<User> = {
              id: userId,
              changes: { ...user, userStatus },
            };
            return [
              UserActions.actionOnUserSuccess({
                update: updatePayload,
                userActionSuccessType:
                  UserActionSuccessTypeEnum.USER_USER_STATUS_UPDATED,
              }),
              UserActions.actionOnUserSuccessReset(),
            ];
          }),
          catchError((error) => {
            console.error(error);
            return [
              UserActions.actionUserFailure({
                error: 'Something went wrong while updating the user status',
              }),
              UserActions.resetUserError(),
            ];
          })
        );
      })
    );
  });

  updateUserTypeAppAccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateUserTypeAppAccess),
      switchMap(({ user, appAccess }) => {
        const userId = user.id || (user.uid as string);

        return this.userService.updateUserTypeAppAccess(userId, appAccess).pipe(
          switchMap(() => {
            const updatePayload: Update<User> = {
              id: userId,
              changes: { ...user, appAccess },
            };
            return [
              UserActions.actionOnUserSuccess({
                update: updatePayload,
                userActionSuccessType:
                  UserActionSuccessTypeEnum.USER_TYPE_APP_ACCESS_UPDATED,
              }),
              UserActions.actionOnUserSuccessReset(),
            ];
          }),
          catchError((error) => {
            console.error(error);
            return [
              UserActions.actionUserFailure({
                error:
                  'Something went wrong while updating the user type app access',
              }),
              UserActions.resetUserError(),
            ];
          })
        );
      })
    );
  });

  updateUserEmail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateUserEmail),
      switchMap(({ user, userEmailUpdateInfo }) => {
        return this.userService.updateUserEmail(userEmailUpdateInfo).pipe(
          switchMap(() => {
            const updatePayload: Update<User> = {
              id: userEmailUpdateInfo.userId,
              changes: { ...user, email: userEmailUpdateInfo.newEmail },
            };
            return [
              UserActions.actionOnUserSuccess({
                update: updatePayload,
                userActionSuccessType:
                  UserActionSuccessTypeEnum.USER_EMAIL_UPDATED,
              }),
              UserActions.actionOnUserSuccessReset(),
            ];
          }),
          catchError((error: any) => {
            return [
              UserActions.actionUserFailure({
                error:
                  error.message ||
                  'Something went wrong while updating the user email',
              }),
              UserActions.resetUserError(),
            ];
          })
        );
      })
    );
  });

  sendResetPasswordEmailForUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.sendResetPasswordEmailForUser),
      switchMap(({ userEmail }) => {
        return this.userService.sendResetPasswordEmailForUser(userEmail).pipe(
          switchMap(() => {
            return [
              UserActions.actionOnUserSuccess({
                userActionSuccessType:
                  UserActionSuccessTypeEnum.USER_PASSWORD_RESET_EMAIL_SENT,
              }),
              UserActions.actionOnUserSuccessReset(),
            ];
          }),
          catchError((error: any) => {
            return [
              UserActions.actionUserFailure({
                error:
                  error.message ||
                  'Something went wrong while sending the password reset email',
              }),
              UserActions.resetUserError(),
            ];
          })
        );
      })
    );
  });

  updateGroupInUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.updateGroupInUser),
      switchMap(({ user, group }) => {
        const userId = user.id || (user.uid as string);

        return this.userService.updateGroupInUser(userId, group).pipe(
          switchMap(() => {
            const updatePayload: Update<User> = {
              id: userId,
              changes: { ...user, group },
            };
            return [
              UserActions.actionOnUserSuccess({
                update: updatePayload,
                userActionSuccessType:
                  UserActionSuccessTypeEnum.GROUP_IN_USER_UPDATED,
              }),
              UserActions.actionOnUserSuccessReset(),
            ];
          }),
          catchError((error) => {
            console.error(error);
            return [
              UserActions.actionUserFailure({
                error: 'Something went wrong while updating the group in user',
              }),
              UserActions.resetUserError(),
            ];
          })
        );
      })
    );
  });

  removeUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.removeUser),
      switchMap(({ userToDelete }) => {
        const userId = userToDelete.id || (userToDelete.uid as string);
        return this.userService.removeUser(userId).pipe(
          switchMap(() => {
            return [
              UserActions.removeUserSuccess({
                userId: userId,
                userActionSuccessType: UserActionSuccessTypeEnum.USER_REMOVED,
              }),
              UserActions.actionOnUserSuccessReset(),
            ];
          }),
          catchError((error) => {
            console.error(error);
            return [
              UserActions.actionUserFailure({
                error: 'Something went wrong while removing the user',
              }),
              UserActions.resetUserError(),
            ];
          })
        );
      })
    );
  });

  loadAdminUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadAdminUsers),
      switchMap(() => {
        return this.userService.getAdminUsers().pipe(
          map((users) => {
            return UserActions.loadUsersSuccess({
              users,
            });
          }),
          catchError((error) => {
            return [UserActions.loadUsersFailure({ error })];
          })
        );
      })
    );
  });

  constructor(private actions$: Actions) { }
}
